import { useMemo } from 'react';
import { useLoaderData, useSearchParams } from 'react-router';
import { useFilterDrawerPreference } from '@modules/search/hook/use-filter-drawer-preference';
import { useAppDisplayModeContext } from '@archipro-design/aria';

import { Spinner } from '@rocco/components/spinner';
import { cn } from '@rocco/utils/cn';
import type { loader as projectsLoader } from '@modules/professional/page/loaders/projects-page-loader.server';
import { NoSearchResults } from '@modules/professional/component/shared/NoSearchResults';
import { SearchFilter } from '@modules/professional/component/shared/SearchFilter';
import { CollapsiblePageLayout } from '~/modules/category/component/collapsible-page-layout/CollapsiblePageLayout';
import { ProfessionalAppliedFilters } from './ProfessionalAppliedFilters';
import { PROFESSIONAL_TAB_PROJECT_FILTERS_CONFIG } from '@modules/professional/config/professional-tab-filter-configs';
import { ProfessionalTabFilterSidebar } from './ProfessionalTabFilterSidebar';
import { useSearchFilters } from '@modules/search/hook/use-search-filters';
import { resetFilterStates } from '~/modules/search/util/reset-filter-states';
import { useMatchesHandleData } from '~/modules/root/hook';
import type { SearchIndex } from '~/modules/search/type';
import { IN_PAGE_SEARCH_IGNORE_FILTER_KEYS } from '~/modules/professional/config/constants';
import { useProfessionalInitialRouteData } from '@modules/professional/hook/use-professional-initial-route-data';
import { useProfessionalTabViewMore } from '@modules/professional/hook/use-professional-tab-view-more';
import { useProfessionalHybridLoad } from '@modules/professional/hook/use-professional-hybrid-load';
import {
    useViewportPageSync,
    viewportPageMarkerProps,
} from '~/ui-rocco/hooks/use-viewport-page-sync';
import { useItemAdminData } from '~/modules/directory/hook/use-item-admin-data';
import { CategoryPageTileWithAdminActions } from '~/ui-rocco/pages/category-page/CategoryPageTileWithAdminActions';
import { LoadMoreButton } from '~/ui-rocco/pages/detail-shared';
import type { TileViewModel } from '@rocco/ui/tile-shared/view-models/tile';
import type { ProjectTileViewModel } from '@rocco/ui/tile-project/view-models/project';
import {
    DIRECTORY_DESKTOP_COL_CLASSES,
    useDirectoryDisplayColumns,
} from '~/ui-rocco/pages/directory-browse/directory-display-columns';
import { DirectoryDisplayDropdown } from '~/ui-rocco/pages/directory-browse/DirectoryDisplayDropdown';
import { TAB_CONTENT_GAP } from '../utils/tab-spacing';
import {
    TAB_RESULTS_PENDING_CLASS,
    useTabResultsPending,
} from '../utils/use-tab-results-pending';

/**
 * Revamped body for the Projects tab (/case-studies) on the site's light
 * section per the Figma: an "(All Projects)" divider, the collapsible left
 * filters reused from the legacy tab (starting collapsed behind a toggle,
 * dark-themed *scoped to this page* via the `[&_*]` overrides), and a 3-column
 * dark editorial grid with infinite scroll. The name / stats / CTA sit in the
 * persistent profile header above, so the tab goes straight to results.
 */
export const ProfessionalProjectsGridSection = () => {
    const {
        projectTilesRocco,
        pagination,
        filterStates,
        filterStatesRocco,
        defaultViewProjectsBy,
        aggregateScopeFilters,
    } = useLoaderData<typeof projectsLoader>();
    const { ShowOwnProjectsOnly, URLSegment, Title } =
        useProfessionalInitialRouteData();
    const { desktop } = useAppDisplayModeContext();
    // Persisted in the site-wide `showFilter` cookie so the show/hide choice
    // survives navigation and stays consistent with the browse pages.
    const { filterDrawerOpen, toggleFilterDrawer } =
        useFilterDrawerPreference();

    // Keyed separately from the `projects` browse grid so a choice made here
    // does not follow the reader out to /projects. Default matches that page.
    const { columns, setColumns } = useDirectoryDisplayColumns(
        'professional-projects',
        3,
    );

    // The sidebar and the sort both navigate; without this the grid sits
    // unchanged for the round trip and a click reads as ignored.
    const resultsPending = useTabResultsPending();

    const {
        tiles: loadedTiles,
        hasMore,
        isLoading,
        loadMore,
        pageStartByIndex,
    } = useProfessionalTabViewMore({
        initialTiles:
            projectTilesRocco as TileViewModel<ProjectTileViewModel>[],
        pagination,
        urlSegment: URLSegment,
        searchType: 'project',
    });

    // `?page=N` follows the page of tiles actually in view, scrolling either
    // direction — not the pages the infinite-scroll lookahead has fetched.
    useViewportPageSync({
        enabled: hasMore || Object.keys(pageStartByIndex).length > 1,
    });

    const [searchParams] = useSearchParams();
    const { showViewMore, onViewMore, infiniteEnabled, sentinelRef } =
        useProfessionalHybridLoad({
            hasMore,
            isLoading,
            loadMore,
            resetKey: searchParams.toString(),
        });

    const adminDataMap = useItemAdminData(
        loadedTiles.map((tile) => tile.model.id),
    );

    // The browse grid's own tile, from the view models the loader already hands
    // us — the same component /projects renders, with the same `projectBrowse`
    // aspect DirectoryBrowseGrid stamps. Round 2 put this tab on the light
    // palette, so the caption takes the light browse ink.
    const tiles: TileViewModel<ProjectTileViewModel>[] = loadedTiles.map(
        (tile) => ({
            ...tile,
            aspectRatio: 'projectBrowse' as const,
            browseTheme: 'light' as const,
            // Names the rail, which is what picks the event: without it the
            // tab reports the generic directoryTileClick where the legacy tab
            // reported ProfessionalProjectClick.
            section: 'ProfessionalProjects',
            adminData: adminDataMap[tile.model.id],
        }),
    );

    const ignoreFilterKeys = useMemo(() => {
        const keys = [...IN_PAGE_SEARCH_IGNORE_FILTER_KEYS];
        if (ShowOwnProjectsOnly) keys.push('viewProjectsBy');
        return keys;
    }, [ShowOwnProjectsOnly]);

    const category = useMatchesHandleData<SearchIndex>('category', 'project');
    const searchFilters = useSearchFilters({
        category,
        initialStates: filterStates,
        isCategoryPage: false,
        ignoreFilterKeys,
    });

    return (
        <section
            className={cn('w-full overflow-x-clip bg-ground text-[#181818]')}
        >
            {/* Desktop keeps the editorial title + intro + CTA cluster; on mobile
                the persistent social-profile header carries the identity/CTAs and
                the tab goes straight to results. */}
            {desktop && (
                <>
                    <div
                        className={cn(
                            'mx-auto w-full max-w-[120rem] md:px-[3.132rem]',
                            TAB_CONTENT_GAP,
                        )}
                    >
                        {/* The heading repeated the tab pill above it; kept as sr-only
                            so the tab still has a heading in the document outline. */}
                        <h2 className="sr-only">Projects</h2>
                    </div>
                    <div className="mx-auto w-full max-w-[120rem] md:px-[3.132rem]">
                        {/* Desktop only: on mobile this is the first thing
                            under the tab strip, whose own border already
                            closes off the nav, so a second rule a hairline
                            below it reads as one thick line. */}
                        <div className="h-px w-full bg-black/20 max-md:hidden" />
                    </div>
                </>
            )}
            {/* Filters + grid, straight to results. */}
            <div className="mx-auto w-full max-w-[120rem] max-md:px-6 max-md:pt-[2.4rem] max-md:pb-14 md:px-[3.132rem] md:pt-[1.5rem] md:pb-[7.5rem]">
                <CollapsiblePageLayout
                    paddingClassName="px-0"
                    sidePanel={
                        desktop &&
                        filterStatesRocco && (
                            // The sidebar self-themes via the directory browse
                            // theme context — no CSS overrides needed here.
                            <div className="h-full md:pt-[1.8rem]">
                                <ProfessionalTabFilterSidebar
                                    searchIndex="project"
                                    config={
                                        PROFESSIONAL_TAB_PROJECT_FILTERS_CONFIG
                                    }
                                    initialFilterStates={filterStatesRocco}
                                    theme="light"
                                    extraIgnoreFilterKeys={
                                        ShowOwnProjectsOnly
                                            ? ['viewProjectsBy']
                                            : undefined
                                    }
                                    defaultViewProjectsBy={
                                        defaultViewProjectsBy ?? undefined
                                    }
                                    aggregateScopeFilters={
                                        aggregateScopeFilters
                                    }
                                />
                            </div>
                        )
                    }
                    hideSidePanel={!filterDrawerOpen}
                >
                    <SearchFilter
                        searchFilters={searchFilters}
                        filterDrawerOpen={filterDrawerOpen}
                        toggleFilterDrawer={toggleFilterDrawer}
                        searchBarVariant="revamp"
                        searchBarTheme="light"
                        hideSort
                        filterControl="plain"
                        searchPlaceholder={`Search Project by ${Title}`}
                        displayControl={
                            desktop ? (
                                <DirectoryDisplayDropdown
                                    columns={columns}
                                    onColumnsChange={setColumns}
                                />
                            ) : null
                        }
                    />
                    {/* The applied-filter bar is desktop-only; on mobile the
                        Refine button's badge + sheet already surface these. */}
                    {desktop &&
                        searchFilters.appliedFilters &&
                        searchFilters.appliedFilters.length > 0 && (
                            <ProfessionalAppliedFilters
                                filterItems={searchFilters.appliedFilters}
                                theme="light"
                                onClearAll={() =>
                                    searchFilters.resetFilters(
                                        resetFilterStates(
                                            searchFilters.filterStates,
                                            [...ignoreFilterKeys, 'category'],
                                        ),
                                    )
                                }
                            />
                        )}

                    {/* Wraps the empty state as well as the grid, so a filter
                        applied over zero results still reads as acknowledged,
                        and holds the paging controls inside the pending state. */}
                    <div
                        className={cn(
                            'transition-opacity duration-200',
                            resultsPending && TAB_RESULTS_PENDING_CLASS,
                        )}
                    >
                        {tiles.length === 0 ? (
                            <div>
                                <NoSearchResults resultsType={'projects'} />
                            </div>
                        ) : (
                            <>
                                <div
                                    className={cn(
                                        'grid grid-cols-1 gap-x-2 gap-y-8 pt-[2.4rem] md:gap-x-3 md:gap-y-[6.25rem]',
                                        DIRECTORY_DESKTOP_COL_CLASSES[columns],
                                    )}
                                >
                                    {tiles.map((tile, index) => {
                                        const card = (
                                            <CategoryPageTileWithAdminActions
                                                key={tile.model.id}
                                                tile={tile}
                                            />
                                        );
                                        const startPage =
                                            pageStartByIndex[index];
                                        return startPage === undefined ? (
                                            card
                                        ) : (
                                            <div
                                                key={tile.model.id}
                                                {...viewportPageMarkerProps(
                                                    startPage,
                                                )}
                                            >
                                                {card}
                                            </div>
                                        );
                                    })}
                                </div>
                                {infiniteEnabled &&
                                    hasMore &&
                                    !resultsPending && (
                                        <div
                                            ref={sentinelRef}
                                            aria-hidden="true"
                                            style={{ height: 1 }}
                                        />
                                    )}
                                {showViewMore && (
                                    <LoadMoreButton
                                        onClick={onViewMore}
                                        isLoading={resultsPending}
                                        label="View more"
                                        className="pt-[3rem]"
                                    />
                                )}
                                {isLoading && (
                                    <div className="flex justify-center pt-[3rem]">
                                        <Spinner className="size-8 text-black" />
                                    </div>
                                )}
                            </>
                        )}
                    </div>
                </CollapsiblePageLayout>
            </div>
        </section>
    );
};
